import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { CircularProgress, IconButton, Stack, Typography } from '@mui/material'
import React, { useCallback, useMemo, useState } from 'react'

import { AssignmentDocumentDTO } from 'models/assignment'
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import { FilterTab } from 'components/common/FilterTab'
import JSZip from 'jszip'
import { MUIButton } from 'components/common/MUIButton'
import { MUIDivider } from 'components/common/MUIDivider'
import { MUITooltip } from 'components/common/MUITooltip'
import { Nullable } from 'models/helpers'
import { RealEstatePropertyDTO } from 'models/property'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'
import { getPropertyPathname } from 'utils/helpers/getPropertyPathname'
import { logger } from 'utils/logging'
import { saveAs } from 'file-saver'
import { useGalleryAssignment } from '../../contexts'
import { useLocation } from 'react-router'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

/**
 * Strips any directory segments to a bare filename. Prevents path-traversal / zip-slip
 * entry names (e.g. `../../evil.pdf`) from ending up in the generated archive or download.
 */
const getSafeFileName = (name: string): string => name.replace(/^.*[\\/]/, '') || 'document'

/** Distinguishes bulk "download all" from per-file downloads in analytics. */
type DownloadMode = 'bulk' | 'single'

/** @interface Props for the ClientGalleryDocumentFiles component. */
interface Props {
  /** Document status history data to display */
  assignmentDocumentsData: AssignmentDocumentDTO[]
  /** Currently selected document */
  selectedDoc?: Nullable<AssignmentDocumentDTO>
  /** Function to set the selected document */
  setSelectedDoc: (doc: AssignmentDocumentDTO) => void
  /** Current assignment ID */
  assignmentId: string
  /** Property data for analytics - only available when rendered in Property page context */
  propertyData?: RealEstatePropertyDTO
}

/*
 * ClientGalleryDocumentFiles component displays the provided files in the client gallery.
 *
 * @example <ClientGalleryDocumentFiles assignmentDocumentsData={[]} />
 */
export const ClientGalleryDocumentFiles: React.FC<Props> = ({
  assignmentDocumentsData,
  selectedDoc,
  setSelectedDoc,
  assignmentId,
  propertyData
}) => {

  const { t } = useTranslation(['gallery'])
  const { pathname } = useLocation()
  const { product } = useGalleryAssignment()
  const { currentUserWorkspace } = useUserData()
  const { spawnErrorToast } = useSnackbar()

  const [isDownloading, setIsDownloading] = useState(false)
  // Keyed by list index (not doc.name, which is not guaranteed unique) so colliding names
  // don't spin/disable each other's rows.
  const [downloadingDocIndexes, setDownloadingDocIndexes] = useState<number[]>([])

  const isPropertyPathname = useMemo(() => getPropertyPathname(pathname), [pathname])

  /** Fetches a single document and returns its Blob. Throws on a non-OK response. */
  const fetchDocumentBlob = async (doc: AssignmentDocumentDTO): Promise<Blob> => {
    const response = await fetch(doc.signedUrl.signedURL)

    if (!response.ok) throw new Error(`Failed to fetch document ${doc.name} (${response.status})`)

    return await response.blob()
  }

  const downloadAllFiles = async () => {
    setIsDownloading(true)

    try {
      const zip = new JSZip()
      const folder = zip.folder(`${assignmentId}`)

      // originalName is user-facing and not guaranteed unique; JSZip keys entries by name,
      // so identical names would silently overwrite each other. De-duplicate with a numeric
      // suffix (and strip any path segments) to keep every document in the archive.
      const usedNames = new Set<string>()
      const toUniqueEntryName = (rawName: string): string => {
        const base = getSafeFileName(rawName)
        if (!usedNames.has(base)) {
          usedNames.add(base)
          return base
        }
        const dotIndex = base.lastIndexOf('.')
        const stem = dotIndex > 0 ? base.slice(0, dotIndex) : base
        const ext = dotIndex > 0 ? base.slice(dotIndex) : ''
        let counter = 1
        let candidate = `${stem} (${counter})${ext}`
        while (usedNames.has(candidate)) {
          counter += 1
          candidate = `${stem} (${counter})${ext}`
        }
        usedNames.add(candidate)
        return candidate
      }

      for (const doc of assignmentDocumentsData) {
        const blob = await fetchDocumentBlob(doc)
        folder?.file(toUniqueEntryName(doc.originalName || doc.signedUrl.filename), blob)
      }

      const content = await zip.generateAsync({ type: 'blob' })
      saveAs(content, `${assignmentId}.zip`)
    } catch (error) {
      logger.error('Failed to download all assignment documents', error, {
        context: {
          component: 'ClientGalleryDocumentFiles',
          action: 'downloadAllFiles',
          metadata: { assignmentId, documentCount: assignmentDocumentsData.length }
        }
      })
      spawnErrorToast(t('gallery:download_all_files_error'), { title: t('gallery:download_error_title') })
    } finally {
      setIsDownloading(false)
    }
  }

  const downloadSingleFile = async (doc: AssignmentDocumentDTO, index: number) => {
    setDownloadingDocIndexes((indexes) => [...indexes, index])

    try {
      const blob = await fetchDocumentBlob(doc)
      saveAs(blob, getSafeFileName(doc.originalName || doc.signedUrl.filename))
    } catch (error) {
      logger.error('Failed to download assignment document', error, {
        context: {
          component: 'ClientGalleryDocumentFiles',
          action: 'downloadSingleFile',
          metadata: { assignmentId, documentName: doc.name }
        }
      })
      spawnErrorToast(t('gallery:download_file_error'), { title: t('gallery:download_error_title') })
    } finally {
      setDownloadingDocIndexes((indexes) => indexes.filter((i) => i !== index))
    }
  }

  const logDownloadDocumentAnalytics = useCallback((mode: DownloadMode) => {
    if (isPropertyPathname && !!propertyData) {
      logAnalyticsEvent(AnalyticsEvent.PROPERTY_DOWNLOAD_DOCUMENTS, {
        mode,
        propertyName: propertyData?.title,
        propertyId: propertyData?.id,
        realtyId: propertyData?.id,
        propertyType: propertyData?.propertyType,
        planId: currentUserWorkspace?.id,
        productKind: product?.kind,
        productCategory: product?.category,
      })
    }
  }, [isPropertyPathname, product?.category, product?.kind, propertyData, currentUserWorkspace?.id])

  return (
    <SectionedBorderBox title={t('provided_files')}>

      <Stack gap="1rem">
        {assignmentDocumentsData.map((doc, index) => {
          const fileName = doc.originalName || doc.signedUrl.filename
          const isDownloadingDoc = downloadingDocIndexes.includes(index)

          return (
            <FilterTab
              key={`${doc.name}-${index}`}
              isActive={selectedDoc?.name === doc.name}
              height="4rem"
              sx={{
                minWidth: 0,
                // .iconLabelWrap (first child) must shrink so the label can truncate
                '& > div:first-of-type': {
                  minWidth: 0,
                  flex: 1,
                  '& > div': { minWidth: 0 }
                }
              }}
              label={
                <MUITooltip content={fileName} placement="top">
                  <Typography variant="text-sm" noWrap sx={{ display: 'block' }}>{fileName}</Typography>
                </MUITooltip>
              }
              badge={
                <MUITooltip content={t('gallery:download')} placement="top">
                  <IconButton
                    size="small"
                    disabled={isDownloadingDoc || isDownloading}
                    sx={{ flexShrink: 0 }}
                    onClick={(e) => {
                      e.stopPropagation()
                      logDownloadDocumentAnalytics('single')
                      downloadSingleFile(doc, index)
                    }}
                  >
                    {isDownloadingDoc
                      ? <CircularProgress size="1.6rem" />
                      : <FileDownloadOutlinedIcon fontSize="small" />
                    }
                  </IconButton>
                </MUITooltip>
              }
              onClick={() => setSelectedDoc(doc)}
            />
          )
        })}

      </Stack>

      <MUIDivider margin={12} />

      <MUIButton
        fullWidth
        type="secondaryBorder"
        size="md"
        startIcon={<CloudDownloadOutlinedIcon fontSize="medium" />}
        isLoading={isDownloading}
        disabled={isDownloading || downloadingDocIndexes.length > 0}
        onClick={() => {
          downloadAllFiles()
          logDownloadDocumentAnalytics('bulk')
        }}
      >
        {t('download_all_files')}
      </MUIButton>

    </SectionedBorderBox>
  )
}
